import React, { useState, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB } from '../../shared/styles';
import { getInitials } from '../../shared/utils';

const FEELINGS = [
  { emoji: '🔥', label: 'Motivated', labelNL: 'Gemotiveerd' },
  { emoji: '💪', label: 'Proud', labelNL: 'Trots' },
  { emoji: '😌', label: 'Calm', labelNL: 'Kalm' },
  { emoji: '🧘', label: 'Focused', labelNL: 'Gefocust' },
  { emoji: '🎉', label: 'Excited', labelNL: 'Enthousiast' },
  { emoji: '😰', label: 'Struggling', labelNL: 'Aan het worstelen' },
  { emoji: '😔', label: 'Tired', labelNL: 'Moe' },
  { emoji: '❤️', label: 'Grateful', labelNL: 'Dankbaar' },
];

function PostComposer({ lang, userName, currentUserId, onPost }) {
  const nl = lang === "nl";
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [feel, setFeel] = useState(null);
  const fileRef = useRef(null);

  const avatarImg = (() => {
    try { return localStorage.getItem(`sl_avatar_${currentUserId}`) || localStorage.getItem('sl_google_picture') || null; } catch { return null; }
  })();

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setImg(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = () => {
    if (!text.trim() && !img) return;
    onPost({ text: text.trim(), img, isPublic, feeling: feel });
    setText(""); setImg(null); setIsPublic(true); setFeel(null); setOpen(false);
  };

  const cancel = () => { setText(""); setImg(null); setIsPublic(true); setFeel(null); setOpen(false); };

  return (
    <div style={{background:_T.bg3,border:`1px solid ${_T.cardBorderBot}`,borderRadius:16,padding:"12px 14px",marginBottom:14}}>
      {/* Collapsed trigger row */}
      {!open && (
        <div style={{display:"flex",gap:10,alignItems:"center",cursor:"text"}} onClick={()=>setOpen(true)}>
          {avatarImg
            ? <img src={avatarImg} alt="" style={{width:34,height:34,borderRadius:"50%",objectFit:"cover",border:`1.5px solid ${_T.accent}44`,flexShrink:0}}/>
            : <div style={{width:34,height:34,borderRadius:"50%",background:`${_T.accent}20`,border:`1px solid ${_T.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:_T.accent,flexShrink:0}}>{getInitials(userName||"Me")}</div>
          }
          <div style={{flex:1,background:_T.bg4,borderRadius:20,padding:"10px 16px",color:C.silver,fontSize:13}}>
            {nl?"Deel je ervaringen...":"Share your experiences..."}
          </div>
          <button onClick={e=>{e.stopPropagation();fileRef.current?.click();}} style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:10,padding:"8px 10px",cursor:"pointer",fontSize:16,lineHeight:1}}>📷</button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleImagePick} style={{display:"none"}}/>
        </div>
      )}

      {/* Expanded composer */}
      {open && (
        <div>
          <div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:10}}>
            {avatarImg
              ? <img src={avatarImg} alt="" style={{width:34,height:34,borderRadius:"50%",objectFit:"cover",border:`1.5px solid ${_T.accent}44`,flexShrink:0,marginTop:3}}/>
              : <div style={{width:34,height:34,borderRadius:"50%",background:`${_T.accent}20`,border:`1px solid ${_T.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:_T.accent,flexShrink:0,marginTop:3}}>{getInitials(userName||"Me")}</div>
            }
            <textarea
              value={text}
              onChange={e=>setText(e.target.value)}
              placeholder={nl?"Schrijf iets over je ervaring...":"Write about your experience..."}
              autoFocus
              style={{flex:1,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:12,padding:"10px 14px",color:C.white,fontSize:13,resize:"none",height:80,fontFamily:"Inter,sans-serif",outline:"none",lineHeight:1.6}}
            />
          </div>

          {/* Feeling selector */}
          <div style={{marginBottom:10}}>
            <div style={{fontSize:10,color:C.silver,letterSpacing:2,fontWeight:600,marginBottom:7}}>{nl?"HOE VOEL JE JE?":"HOW ARE YOU FEELING?"}</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {FEELINGS.map(f => {
                const sel = feel?.label === f.label;
                return (
                  <button key={f.label} onClick={()=>setFeel(sel?null:f)} style={{background:sel?`${_T.accent}22`:_T.bg4,border:`1px solid ${sel?_T.accent:_T.cardBorderBot}`,borderRadius:20,padding:"5px 9px",cursor:"pointer",display:"flex",alignItems:"center",gap:4,transition:"all 0.15s"}}>
                    <span style={{fontSize:13}}>{f.emoji}</span>
                    <span style={{fontSize:10,color:sel?_T.accent:C.silver,fontWeight:sel?700:400}}>{nl?f.labelNL:f.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Image preview */}
          {img && (
            <div style={{position:"relative",marginBottom:10}}>
              <img src={img} alt="" style={{width:"100%",maxHeight:200,objectFit:"cover",borderRadius:12,border:`1px solid ${_T.cardBorderBot}`}}/>
              <button onClick={()=>setImg(null)} style={{position:"absolute",top:8,right:8,background:"rgba(0,0,0,0.6)",border:"none",borderRadius:"50%",width:26,height:26,color:C.white,cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>
          )}

          {/* Controls row */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <button onClick={()=>fileRef.current?.click()} style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:10,padding:"7px 12px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:5}}>
              📷 {nl?"Foto":"Photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImagePick} style={{display:"none"}}/>

            {/* Public/Private toggle */}
            <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto"}}>
              <span style={{fontSize:11,color:C.silver}}>{isPublic?(nl?"Publiek":"Public"):(nl?"Privé":"Private")}</span>
              <button onClick={()=>setIsPublic(p=>!p)} style={{width:44,height:24,borderRadius:12,background:isPublic?_T.accent:_T.bg4,border:`1px solid ${isPublic?_T.accent:_T.cardBorderBot}`,cursor:"pointer",position:"relative",padding:0}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:C.white,position:"absolute",top:3,left:isPublic?23:3,transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.4)"}}/>
              </button>
              <span style={{fontSize:16}}>{isPublic?"🌍":"🔒"}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{display:"flex",gap:8}}>
            <button onClick={cancel} style={s_GB({flex:1,padding:"9px",fontSize:13})}>
              {nl?"Annuleren":"Cancel"}
            </button>
            <button
              onClick={submit}
              disabled={!text.trim()&&!img}
              style={{...s_PB({flex:1,padding:"9px",fontSize:13}),opacity:(!text.trim()&&!img)?0.4:1}}
            >
              {nl?"Delen":"Post"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostComposer;
